/*
 * Code generated by Speakeasy (https://speakeasy.com). DO NOT EDIT.
 */

import { domainsCheckHealth } from "../funcs/domainsCheckHealth.js";
import { domainsDeleteDomain } from "../funcs/domainsDeleteDomain.js";
import { domainsGetDomain } from "../funcs/domainsGetDomain.js";
import { domainsListDomains } from "../funcs/domainsListDomains.js";
import { domainsListMcpEndpoints } from "../funcs/domainsListMcpEndpoints.js";
import { domainsListRootMcpServers } from "../funcs/domainsListRootMcpServers.js";
import { domainsRegisterDomain } from "../funcs/domainsRegisterDomain.js";
import { domainsSetRootMcpEndpoint } from "../funcs/domainsSetRootMcpEndpoint.js";
import { domainsUpdateDomain } from "../funcs/domainsUpdateDomain.js";
import { ClientSDK, RequestOptions } from "../lib/sdks.js";
import { CustomDomain } from "../models/components/customdomain.js";
import { ListCustomDomainMcpEndpointsResult } from "../models/components/listcustomdomainmcpendpointsresult.js";
import { ListCustomDomainsResult } from "../models/components/listcustomdomainsresult.js";
import { ListRootMcpServersResponseBody } from "../models/components/listrootmcpserversresponsebody.js";
import {
  CheckDomainHealthRequest,
  CheckDomainHealthSecurity,
} from "../models/operations/checkdomainhealth.js";
import {
  DeleteDomainRequest,
  DeleteDomainSecurity,
} from "../models/operations/deletedomain.js";
import {
  GetDomainRequest,
  GetDomainSecurity,
} from "../models/operations/getdomain.js";
import {
  ListCustomDomainMcpEndpointsRequest,
  ListCustomDomainMcpEndpointsSecurity,
} from "../models/operations/listcustomdomainmcpendpoints.js";
import {
  ListDomainsRequest,
  ListDomainsSecurity,
} from "../models/operations/listdomains.js";
import {
  ListRootMcpServersRequest,
  ListRootMcpServersSecurity,
} from "../models/operations/listrootmcpservers.js";
import {
  RegisterDomainRequest,
  RegisterDomainSecurity,
} from "../models/operations/registerdomain.js";
import {
  SetRootMcpEndpointRequest,
  SetRootMcpEndpointSecurity,
} from "../models/operations/setrootmcpendpoint.js";
import {
  UpdateDomainRequest,
  UpdateDomainSecurity,
} from "../models/operations/updatedomain.js";
import { unwrapAsync } from "../types/fp.js";

export class Domains extends ClientSDK {
  /**
   * checkHealth domains
   *
   * @remarks
   * Check the routing and certificate health of the organization's custom domain
   */
  async checkHealth(
    request?: CheckDomainHealthRequest | undefined,
    security?: CheckDomainHealthSecurity | undefined,
    options?: RequestOptions,
  ): Promise<CustomDomain> {
    return unwrapAsync(domainsCheckHealth(
      this,
      request,
      security,
      options,
    ));
  }

  /**
   * deleteDomain domains
   *
   * @remarks
   * Delete a custom domain
   */
  async deleteDomain(
    request?: DeleteDomainRequest | undefined,
    security?: DeleteDomainSecurity | undefined,
    options?: RequestOptions,
  ): Promise<void> {
    return unwrapAsync(domainsDeleteDomain(
      this,
      request,
      security,
      options,
    ));
  }

  /**
   * getDomain domains
   *
   * @remarks
   * Get the custom domain for an organization
   */
  async getDomain(
    request?: GetDomainRequest | undefined,
    security?: GetDomainSecurity | undefined,
    options?: RequestOptions,
  ): Promise<CustomDomain> {
    return unwrapAsync(domainsGetDomain(
      this,
      request,
      security,
      options,
    ));
  }

  /**
   * listDomains domains
   *
   * @remarks
   * List the custom domains for an organization. The result is empty when no custom domain has been configured.
   */
  async listDomains(
    request?: ListDomainsRequest | undefined,
    security?: ListDomainsSecurity | undefined,
    options?: RequestOptions,
  ): Promise<ListCustomDomainsResult> {
    return unwrapAsync(domainsListDomains(
      this,
      request,
      security,
      options,
    ));
  }

  /**
   * listMcpEndpoints domains
   *
   * @remarks
   * List the MCP endpoints registered under the organization's custom domain across every project. Returns enriched rows that include the parent MCP server and project so callers can preview what a custom-domain deletion would cascade through.
   */
  async listMcpEndpoints(
    request?: ListCustomDomainMcpEndpointsRequest | undefined,
    security?: ListCustomDomainMcpEndpointsSecurity | undefined,
    options?: RequestOptions,
  ): Promise<ListCustomDomainMcpEndpointsResult> {
    return unwrapAsync(domainsListMcpEndpoints(
      this,
      request,
      security,
      options,
    ));
  }

  /**
   * listRootMcpServers domains
   *
   * @remarks
   * List the organization's MCP servers that can be mapped to the custom domain root, including servers not yet attached to the domain
   */
  async listRootMcpServers(
    request?: ListRootMcpServersRequest | undefined,
    security?: ListRootMcpServersSecurity | undefined,
    options?: RequestOptions,
  ): Promise<ListRootMcpServersResponseBody> {
    return unwrapAsync(domainsListRootMcpServers(
      this,
      request,
      security,
      options,
    ));
  }

  /**
   * createDomain domains
   *
   * @remarks
   * Create a custom domain for an organization
   */
  async registerDomain(
    request: RegisterDomainRequest,
    security?: RegisterDomainSecurity | undefined,
    options?: RequestOptions,
  ): Promise<CustomDomain> {
    return unwrapAsync(domainsRegisterDomain(
      this,
      request,
      security,
      options,
    ));
  }

  /**
   * setRootMcpEndpoint domains
   *
   * @remarks
   * Set or clear the MCP endpoint mapped to a custom domain's root. Pass mcp_endpoint_id for an endpoint already attached to the domain, or mcp_server_id to attach a server (creating its domain endpoint if needed) and map it in one call — usable while the domain is still pending verification, so a migration can be staged before DNS cuts over.
   */
  async setRootMcpEndpoint(
    request: SetRootMcpEndpointRequest,
    security?: SetRootMcpEndpointSecurity | undefined,
    options?: RequestOptions,
  ): Promise<CustomDomain> {
    return unwrapAsync(domainsSetRootMcpEndpoint(
      this,
      request,
      security,
      options,
    ));
  }

  /**
   * updateDomain domains
   *
   * @remarks
   * Update settings for the organization's custom domain
   */
  async updateDomain(
    request: UpdateDomainRequest,
    security?: UpdateDomainSecurity | undefined,
    options?: RequestOptions,
  ): Promise<CustomDomain> {
    return unwrapAsync(domainsUpdateDomain(
      this,
      request,
      security,
      options,
    ));
  }
}
